import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Flex } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  SbButton, SbInput, SbTag, SbSpin, SbNotification, SbModal,
  SbRow, SbCol, BUTTON_TYPE, PlusOutlined,
} from 'uiCore';
import SbDivider from 'uiCore/SbDivider/SbDivider';
import SbCollapse from 'uiCore/SbCollapse/SbCollapse';
import { getOrgTechContext, saveOrgTechContext } from 'Api/apiService';
import { generateKey, getAvatarInitials } from 'Utils/orgContextUtils';
import { useDraftState } from 'Hooks/useDraftState';
import { useCollapseState } from '../OrgTechContextHelper/helpers';
import InlineAddInput from '../OrgTechContextHelper/InlineAddInput';
import ExpandCollapseButtons from '../OrgTechContextHelper/ExpandCollapseButtons';
import Permission from 'Components/Permission';
import { useHasAccess } from 'Routes/hooks/useHasAccess';
import NotAuthorized from 'Pages/NotAuthorized';
import {
  PageWrapperStyled, FunctionAvatarStyled, InputWrapperStyled, FooterStyled,
} from 'Components/OrgContext/OrgTechContextHelper/orgContextStyles'
import {
  PageTitleStyled, StatsBarStyled, CategorySectionStyled, AddRowStyled, BackHeaderStyled,
} from './OrgRegistryManagement.styles';

const { Text, Title } = Typography;

// API stores catalog keyed by function; UI renders an ordered list.
const catalogToList = (catalog) => Object.values(catalog ?? {});
const listToCatalog = (list) =>
  Object.fromEntries(list.map((func) => [func.function_key, func]));

// Single source of truth for draft input keys — used by handlers and inputs alike.
const draftKey = {
  newFunction: 'new_function',
  newCategory: (funcKey) => `cat_${funcKey}`,
  newTool: (catKey) => `tool_${catKey}`,
};

const countTools = (categories) =>
  categories.reduce((sum, cat) => sum + cat.tools.length, 0);

const OrgRegistryManagement = () => {
  const navigate = useNavigate();
  const { hasAccess } = useHasAccess();
  const [functionList, setFunctionList] = useState([]);
  const [isAddingFunction, setIsAddingFunction] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { getDraft, setDraft, clearDraft } = useDraftState();
  const { expandedKeys, setExpandedKeys, expandAll, collapseAll } = useCollapseState();

  // Access is driven by the org_registry_management entitlement, not a hardcoded role name
  const hasOrgRegistryAccess = hasAccess('system_admin_setup-org_registry_management', ['view']);

  // Don't request org data for users who will only see Not Authorized
  useEffect(() => {
    if (!hasOrgRegistryAccess) return;
    getOrgTechContext()
      .then((data) => setFunctionList(catalogToList(data?.context_json)))
      .finally(() => setIsLoading(false));
  }, [hasOrgRegistryAccess]);


  const totalStats = useMemo(() => {
    let totalCategories = 0;
    let totalTools = 0;
    functionList.forEach((func) => {
      totalCategories += func.categories.length;
      totalTools += countTools(func.categories);
    });
    return { totalFunctions: functionList.length, totalCategories, totalTools };
  }, [functionList]);

  // Find one function by key and replace it with the result of `transform`. Other functions pass through unchanged.
  const updateFunction = (funcKey, transform) =>
    setFunctionList((prev) =>
      prev.map((func) => (func.function_key === funcKey ? transform(func) : func)),
    );

  const handleAddFunction = () => {
    const name = getDraft(draftKey.newFunction).trim();
    if (!name) return;
    const newKey = generateKey(name);
    if (functionList.some((func) => func.function_key === newKey)) return;
    setFunctionList((prev) => [...prev, { function_key: newKey, function_name: name, categories: [] }]);
    setExpandedKeys((prev) => [...prev, newKey]);
    clearDraft(draftKey.newFunction);
    setIsAddingFunction(false);
  };

  const handleAddCategory = (funcKey) => {
    const key = draftKey.newCategory(funcKey);
    const name = getDraft(key).trim();
    if (!name) return;
    const newCatKey = generateKey(name);
    updateFunction(funcKey, (func) => {
      if (func.categories.some((cat) => cat.category_key === newCatKey)) return func;
      return {
        ...func,
        categories: [...func.categories, { category_key: newCatKey, category_name: name, tools: [] }],
      };
    });
    clearDraft(key);
  };

  const handleAddTool = (funcKey, catKey) => {
    const key = draftKey.newTool(catKey);
    const name = getDraft(key).trim();
    if (!name) return;
    const newToolKey = generateKey(name);
    updateFunction(funcKey, (func) => ({
      ...func,
      categories: func.categories.map((cat) => {
        if (cat.category_key !== catKey) return cat;
        if (cat.tools.some((tool) => tool.tool_key === newToolKey)) return cat;
        return { ...cat, tools: [...cat.tools, { tool_key: newToolKey, tool_name: name }] };
      }),
    }));
    clearDraft(key);
  };

  const hasMinimumFunctions = functionList.length >= 3;

  const handleSaveClick = () => {
    if (!hasMinimumFunctions) return;

    SbModal.confirm({
      title: 'Confirm Save Configuration',
      content: 'Are you sure you want to save the master configuration? This will update the organization context library.',
      okText: 'Save',
      cancelText: 'Cancel',
      onOk: async () => {
        setIsSaving(true);
        try {
          await saveOrgTechContext({ context: listToCatalog(functionList) });
          SbNotification.success('Saved', 'Master configuration has been updated.');
        } catch {
          SbNotification.error('Error', 'Failed to save master configuration.');
        } finally {
          setIsSaving(false);
        }
      },
    });
  };

  // Authorization check - must be after all hooks
  if (!hasOrgRegistryAccess) {
    return <NotAuthorized />;
  }

  const collapseItems = functionList.map((func, index) => ({
    key: func.function_key,
    label: (
      <Flex align="center" gap={12} flex={1}>
        <FunctionAvatarStyled $index={index}>
          {getAvatarInitials(func.function_name)}
        </FunctionAvatarStyled>
        <div>
          <Text strong>{func.function_name}</Text>
          <br />
          <Text type="secondary">
            {func.categories.length} {func.categories.length === 1 ? 'category' : 'categories'}
            {' · '}
            {countTools(func.categories)} tools
          </Text>
        </div>
      </Flex>
    ),
    children: (
      <>
        {func.categories.map((cat) => {
          const toolDraftKey = draftKey.newTool(cat.category_key);
          return (
            <CategorySectionStyled key={cat.category_key}>
              <SbDivider orientation="left" plain>
                <Text type="secondary">{cat.category_name}</Text>
              </SbDivider>

              {cat.tools.length > 0 && (
                <SbRow gutter={[8, 8]}>
                  {cat.tools.map((tool) => (
                    <SbCol key={tool.tool_key}>
                      <SbTag>{tool.tool_name}</SbTag>
                    </SbCol>
                  ))}
                </SbRow>
              )}

              <Permission resource="system_admin_setup-org_registry_management" actions={["edit"]}>
                <InputWrapperStyled>
                  <InlineAddInput
                    placeholder={`Add tool to ${cat.category_name}`}
                    value={getDraft(toolDraftKey)}
                    onChange={(value) => setDraft(toolDraftKey, value)}
                    onSubmit={() => handleAddTool(func.function_key, cat.category_key)}
                  />
                </InputWrapperStyled>
              </Permission>
            </CategorySectionStyled>
          );
        })}

        <Permission resource="system_admin_setup-org_registry_management" actions={["edit"]}>
          <AddRowStyled align="center" gap={8}>
            <InputWrapperStyled>
              <InlineAddInput
                placeholder={`Add category to ${func.function_name}`}
                value={getDraft(draftKey.newCategory(func.function_key))}
                onChange={(value) => setDraft(draftKey.newCategory(func.function_key), value)}
                onSubmit={() => handleAddCategory(func.function_key)}
              />
            </InputWrapperStyled>
          </AddRowStyled>
        </Permission>
      </>
    ),
  }));

  return (
    <SbSpin spinning={isLoading || isSaving}>
      <PageWrapperStyled>
        <BackHeaderStyled align="center" gap={12}>
          <SbButton
            buttonType={BUTTON_TYPE.SECONDARY}
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/system-admin-setup')}
            aria-label="Back to Home"
          />
          <Title level={5}>Org Registry Management</Title>
        </BackHeaderStyled>
        <StatsBarStyled align="center" wrap="wrap" gap={8} justify="space-between">
          <Flex align="center" gap={8}>
            <SbTag color="blue">{totalStats.totalFunctions} Functions</SbTag>
            <SbTag>{totalStats.totalCategories} Categories</SbTag>
            <SbTag color="gold">{totalStats.totalTools} Tools</SbTag>
          </Flex>
          <Flex align="center" gap={8}>
            <ExpandCollapseButtons
              expandAll={() => expandAll(functionList)}
              collapseAll={collapseAll}
            />
          </Flex>
        </StatsBarStyled>

        <PageTitleStyled>
          <Title level={5}>Org Context Library</Title>
          <Text type="secondary">
            Manage functions, categories, and tools available for all organizations.
          </Text>
        </PageTitleStyled>

        {isAddingFunction ? (
          <Permission resource="system_admin_setup-org_registry_management" actions={["edit"]}>
            <AddRowStyled align="center" gap={8}>
              <InputWrapperStyled>
                <SbInput
                  autoFocus
                  placeholder="Function name — press Enter to add"
                  value={getDraft(draftKey.newFunction)}
                  onChange={(e) => setDraft(draftKey.newFunction, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddFunction();
                    if (e.key === 'Escape') setIsAddingFunction(false);
                  }}
                  suffix={<PlusOutlined onClick={handleAddFunction} />}
                />
              </InputWrapperStyled>
              <SbButton buttonType={BUTTON_TYPE.SECONDARY} onClick={() => setIsAddingFunction(false)}>
                Cancel
              </SbButton>
            </AddRowStyled>
          </Permission>
        ) : (
          <Permission resource="system_admin_setup-org_registry_management" actions={["edit"]}>
            <AddRowStyled>
              <SbButton
                buttonType={BUTTON_TYPE.SECONDARY}
                icon={<PlusOutlined />}
                onClick={() => setIsAddingFunction(true)}
              >
                Add Function
              </SbButton>
            </AddRowStyled>
          </Permission>
        )}

        <SbCollapse
          activeKey={expandedKeys}
          onChange={setExpandedKeys}
          items={collapseItems}
        />

        <FooterStyled>
          <Flex vertical gap={8}>
            {!hasMinimumFunctions && (
              <Text type="danger">
                Add at least 3 functions to save
              </Text>
            )}
            <Permission resource="system_admin_setup-org_registry_management" actions={["edit"]}>
              <SbButton
                buttonType={BUTTON_TYPE.PRIMARY}
                onClick={handleSaveClick}
                loading={isSaving}
                disabled={!hasMinimumFunctions}
              >
                Save Configuration
              </SbButton>
            </Permission>
          </Flex>
        </FooterStyled>
      </PageWrapperStyled>
    </SbSpin>
  );
};

export default OrgRegistryManagement;
